import { useState, useRef, useEffect } from 'react'
import { useCrearTareaMutation } from '../hooks/useTareasQueries'
import { useCrearNotaMutation } from '../hooks/useNotasQueries'
import { useCrearTransaccionMutation } from '../hooks/useFinanzasQueries'
import { useCrearHabitoMutation } from '../hooks/useHabitosQueries'
import { format } from 'date-fns'
import { canUseAIFeatures } from '../utils/freemiumLimits'
import { useAuthStore } from '../store/useAuthStore'
import { useWorkspaceStore } from '../store/useWorkspaceStore'
import UpgradeModal from './UpgradeModal'
import apiClient from '../api/axios'

const Asistente = () => {
  const { mutate: agregarTarea } = useCrearTareaMutation()
  const { mutate: agregarNota } = useCrearNotaMutation()
  const { mutate: agregarTransaccion } = useCrearTransaccionMutation()
  const { mutate: agregarHabito } = useCrearHabitoMutation()
  
  const { user } = useAuthStore()
  const { activeWorkspaceId } = useWorkspaceStore()
  const [showUpgradeModal, setShowUpgradeModal] = useState(false)
  const [abierto, setAbierto]       = useState(false)
  const [input, setInput]           = useState('')
  const [mensajes, setMensajes]     = useState([
    { rol: 'asistente', texto: '¡Hola! Soy tu asistente de DayFlow. Podés decirme cosas como "recordame estudiar mañana a las 18" o "gasté 5000 en comida" y lo registro automáticamente.' }
  ])
  const [cargando, setCargando]     = useState(false)
  const [escuchando, setEscuchando] = useState(false)
  const inputRef   = useRef(null)
  const bottomRef  = useRef(null)
  const recognRef  = useRef(null)

  useEffect(() => {
    if (abierto) {
      setTimeout(() => inputRef.current?.focus(), 100)
    }
  }, [abierto])

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [mensajes])

  const procesarAccion = (json) => {
    switch (json.accion) {
      case 'agregar_tarea': {
        const nueva = {
          nombre: json.nombre, descripcion: json.descripcion || '',
          fecha: json.fecha || '', hora: json.hora || '',
          prioridad: json.prioridad || 'media',
          workspace_id: activeWorkspaceId
        }
        agregarTarea(nueva)
        return `✅ Tarea agregada: "${json.nombre}"${json.fecha ? ` para el ${json.fecha}` : ''}${json.hora ? ` a las ${json.hora}` : ''}`
      }
      case 'agregar_nota': {
        const nueva = {
          titulo: json.titulo || '',
          contenido: json.contenido,
          relevancia: json.relevancia || 'media',
          workspace_id: activeWorkspaceId
        }
        agregarNota(nueva)
        return `📝 Nota guardada${json.titulo ? `: "${json.titulo}"` : ''}`
      }
      case 'agregar_gasto': {
        const nuevo = {
          tipo: 'gasto',
          monto: parseFloat(json.monto),
          descripcion: json.descripcion,
          categoria: json.categoria || 'otro',
          fecha: format(new Date(), 'yyyy-MM-dd'),
          recurrente: false,
          workspace_id: activeWorkspaceId
        }
        agregarTransaccion(nuevo)
        return `💸 Gasto registrado: $${json.monto} en ${json.descripcion}`
      }
      case 'agregar_ingreso': {
        const nuevo = {
          tipo: 'ingreso',
          monto: parseFloat(json.monto),
          descripcion: json.descripcion,
          categoria: 'otro',
          fecha: format(new Date(), 'yyyy-MM-dd'),
          recurrente: false,
          workspace_id: activeWorkspaceId
        }
        agregarTransaccion(nuevo)
        return `💰 Ingreso registrado: $${json.monto} — ${json.descripcion}`
      }
      case 'agregar_habito': {
        const nuevo = {
          nombre: json.nombre,
          emoji: json.emoji || '⭐',
          color: 'blue',
          workspace_id: activeWorkspaceId
        }
        agregarHabito(nuevo)
        return `🔥 Hábito agregado: "${json.nombre}"`
      }
      case 'consulta':
        return json.respuesta
      case 'no_entendido':
        return json.respuesta
      default:
        return 'No entendí bien eso, ¿podés reformularlo?'
    }
  }

  const enviar = async (texto) => {
    if (!texto.trim() || cargando) return
    const nuevosMensajes = [...mensajes, { rol: 'usuario', texto }]
    setMensajes(nuevosMensajes)
    setInput('')
    setCargando(true)

    try {
      const { data } = await apiClient.post('/assistant/message', {
        text: texto,
        hoy: format(new Date(), 'yyyy-MM-dd (EEEE)'),
      })
      const raw = data.text || '{}'
      const match = raw.match(/\{[\s\S]*\}/)
      if (!match) throw new Error('No JSON encontrado')
      const json = JSON.parse(match[0])
      const respuesta = procesarAccion(json)
      setMensajes(prev => [...prev, { rol: 'asistente', texto: respuesta }])
    } catch (err) {
      setMensajes(prev => [...prev, {
        rol: 'asistente',
        texto: 'Hubo un error al conectarme. Verificá tu conexión o la API key.'
      }])
    } finally {
      setCargando(false)
    }
  }

  const toggleVoz = () => {
    if (!('webkitSpeechRecognition' in window || 'SpeechRecognition' in window)) {
      alert('Tu navegador no soporta reconocimiento de voz. Probá con Chrome.')
      return
    }
    if (escuchando) {
      recognRef.current?.stop()
      setEscuchando(false)
      return
    }
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition
    const recog = new SR()
    recog.lang = 'es-AR'
    recog.continuous = false
    recog.interimResults = false
    recog.onresult = (e) => {
      const texto = e.results[0][0].transcript
      setInput(texto)
      setEscuchando(false)
      setTimeout(() => enviar(texto), 300)
    }
    recog.onerror = () => setEscuchando(false)
    recog.onend   = () => setEscuchando(false)
    recognRef.current = recog
    recog.start()
    setEscuchando(true)
  }

  const toggleAsistente = () => {
    if (!abierto && !canUseAIFeatures(user?.plan)) {
      setShowUpgradeModal(true)
      return
    }
    setAbierto(!abierto)
  }

  return (
    <>
      {/* Botón flotante */}
      <button
        onClick={toggleAsistente}
        className={`fixed bottom-6 right-6 w-14 h-14 rounded-full shadow-lg flex items-center justify-center text-2xl transition-all z-50
          ${abierto ? 'bg-gray-700 rotate-45' : 'bg-gradient-to-r from-indigo-500 to-purple-600 hover:scale-105 shadow-indigo-500/50'}`}
      >
        {abierto ? '×' : '✨'}
      </button>

      {/* Panel del asistente (Glassmorphism) */}
      {abierto && (
        <div className="fixed bottom-24 right-6 w-80 bg-white/70 dark:bg-gray-900/70 backdrop-blur-xl border border-white/40 dark:border-white/10 rounded-[2rem] shadow-2xl z-50 flex flex-col overflow-hidden"
          style={{ height: '450px' }}>

          {/* Header */}
          <div className="px-4 py-4 border-b border-gray-200/50 dark:border-gray-800/50 flex items-center gap-3 relative">
            <div className="absolute -left-4 -top-4 w-24 h-24 bg-indigo-500/20 rounded-full blur-2xl pointer-events-none" />
            <div className="absolute -right-4 -bottom-4 w-24 h-24 bg-purple-500/20 rounded-full blur-2xl pointer-events-none" />
            <div className="w-10 h-10 rounded-full bg-gradient-to-tr from-indigo-500 to-purple-500 flex items-center justify-center text-lg shadow-lg shadow-indigo-500/30 z-10">✨</div>
            <div className="z-10">
              <p className="text-sm font-bold text-gray-800 dark:text-gray-100 uppercase tracking-widest">DayFlow AI</p>
              <p className="text-[10px] font-medium text-indigo-600 dark:text-indigo-400">Copiloto Premium</p>
            </div>
          </div>

          {/* Mensajes */}
          <div className="flex-1 overflow-y-auto px-4 py-3 flex flex-col gap-3">
            {mensajes.map((m, i) => (
              <div key={i} className={`flex ${m.rol === 'usuario' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-xs px-3 py-2 rounded-2xl text-sm leading-relaxed
                  ${m.rol === 'usuario'
                    ? 'bg-indigo-600 text-white rounded-br-sm'
                    : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-200 rounded-bl-sm'
                  }`}>
                  {m.texto}
                </div>
              </div>
            ))}
            {cargando && (
              <div className="flex justify-start">
                <div className="bg-gray-100 dark:bg-gray-800 px-4 py-2 rounded-2xl rounded-bl-sm">
                  <div className="flex gap-1 items-center h-4">
                    {[0,1,2].map(i => (
                      <div key={i} className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce"
                        style={{ animationDelay: `${i * 0.15}s` }} />
                    ))}
                  </div>
                </div>
              </div>
            )}
            <div ref={bottomRef} />
          </div>

          {/* Sugerencias rápidas */}
          <div className="px-3 py-2 flex gap-1.5 overflow-x-auto border-t border-gray-100 dark:border-gray-800">
            {[
              'Recordame estudiar mañana',
              'Gasté 3000 en comida',
              'Nueva nota: idea de proyecto',
            ].map(s => (
              <button key={s} onClick={() => enviar(s)}
                className="text-xs px-2.5 py-1 rounded-full bg-indigo-50 dark:bg-indigo-950 text-indigo-600 dark:text-indigo-400 border border-indigo-100 dark:border-indigo-900 whitespace-nowrap hover:bg-indigo-100 dark:hover:bg-indigo-900 transition-colors flex-shrink-0">
                {s}
              </button>
            ))}
          </div>

          {/* Input */}
          <div className="px-3 py-3 border-t border-gray-100 dark:border-gray-800 flex gap-2">
            <button onClick={toggleVoz}
              className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 transition-all border
                ${escuchando
                  ? 'bg-red-500 border-red-500 text-white animate-pulse'
                  : 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-500'
                }`}>
              🎤
            </button>
            <input
              ref={inputRef}
              type="text"
              placeholder="Escribí o hablá..."
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && enviar(input)}
              className="flex-1 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl px-3 py-2 text-sm outline-none focus:border-indigo-400 text-gray-700 dark:text-gray-200 placeholder-gray-400"
            />
            <button onClick={() => enviar(input)}
              disabled={!input.trim() || cargando}
              className="w-9 h-9 rounded-xl bg-indigo-600 hover:bg-indigo-700 disabled:opacity-40 flex items-center justify-center flex-shrink-0 transition-colors text-white text-sm">
              ↑
            </button>
          </div>
        </div>
      )}

      <UpgradeModal isOpen={showUpgradeModal} onClose={() => setShowUpgradeModal(false)} featureName="Asistente de IA Premium" />
    </>
  )
}

export default Asistente
